import './Theme.scss'

import {
	ButtonData,
	Button as ButtonDefault,
	ButtonIcons
} from '../views/ui/Button'
import {LinkProps, Link as UtilLink} from '../util/link'
import {Mod, style} from '../util/style'
import React, {HTMLProps} from 'react'

import {Icon} from './ui/Icon'
import {Trusted} from '../util/trusted'
import {createTheme} from '../util/createtheme'
import {px} from 'util/units'
import {scrollbarWidth} from 'util/scrollbar'

type Div = HTMLProps<HTMLDivElement>
type Span = HTMLProps<HTMLSpanElement>

const styles = style('theme', [
	'container',
	'h1',
	'h2',
	'h3',
	'h4',
	'h5',
	'p',
	'wysiwyg',
	'button',
	'buttontext',
	'buttonicon',
	'link',
	'linktext',
	'linkicon'
])

const Container: React.FC<
	Div & {mod?: Mod<'mobile_full'>; shifted?: 'left' | 'right'}
> = ({children, mod, shifted, ...props}) => {
	return (
		<div {...props} className={styles.container.mod(mod).mod({shifted})}>
			{!!shifted && <ContainerShifter side={shifted} />}
			{children}
		</div>
	)
}

const ContainerShifter: React.FC<Div & {side?: 'left' | 'right'}> = ({
	side
}) => {
	const scrollBarWidth = scrollbarWidth()
	return (
		<div
			className={styles.container('shifter')}
			style={{
				width: `calc((100vw - 100%) / 2)`,
				right:
					side === 'right' &&
					`calc((100vw - 100% - ${px(scrollBarWidth)}) / -2)`,
				left:
					side === 'left' &&
					`calc((100vw - 100% - ${px(scrollBarWidth)}) / -2 )`
			}}
		/>
	)
}

type Heading = React.FC<
	HTMLProps<HTMLHeadingElement> & {as?: any; mod?: Mod<HeadingMods>}
>

export type HeadingMods =
	| 'bold'
	| 'inherit'
	| 'grey'
	| 'green'
	| 'white'
	| 'marked'
	| 'slantleft'
	| 'slantright'

const H1: Heading = ({as: Tag = 'h1', mod, ...props}) => (
	<div className={props.className}>
		<Tag {...props} className={styles.h1().mod(mod)} />
	</div>
)
const H2: Heading = ({as: Tag = 'h2', mod, ...props}) => (
	<div className={props.className}>
		<Tag {...props} className={styles.h2().mod(mod)} />
	</div>
)
const H3: Heading = ({as: Tag = 'h3', mod, ...props}) => (
	<div className={props.className}>
		<Tag {...props} className={styles.h3().mod(mod)} />
	</div>
)
const H4: Heading = ({as: Tag = 'h4', mod, ...props}) => (
	<div className={props.className}>
		<Tag {...props} className={styles.h4().mod(mod)} />
	</div>
)
const H5: Heading = ({as: Tag = 'h5', mod, ...props}) => (
	<div className={props.className}>
		<Tag {...props} className={styles.h5().mod(mod)} />
	</div>
)

const P: React.FC<HTMLProps<HTMLParagraphElement>> = (props) => (
	<p {...props} className={styles.p.mergeProps(props)} />
)

const Wysiwyg: React.FC<{children: string} & any> = (props) => (
	<Trusted {...props} className={styles.wysiwyg.mergeProps(props)} />
)

type ButtonIconType = any

const Button: React.FC<ButtonData & {iconafter?: ButtonIcons}> = ({
	mod,
	className,
	iconafter,
	withIcon,
	children,
	...props
}) => {
	return (
		<ButtonDefault
			{...props}
			className={styles.button.with(className).mod(mod).toString()}
			withIcon={iconafter || withIcon}
			mod={mod}
		>
			<ButtonText>{children}</ButtonText>
		</ButtonDefault>
	)
}

const ButtonText: React.FC = (props) => (
	<span {...props} className={styles.buttontext.mergeProps(props)} />
)

const ButtonIcon: React.FC<{icon: ButtonIconType} & Span> = ({
	icon,
	...props
}) => (
	<span {...props} className={styles.buttonicon.mergeProps(props)}>
		<Icon icon={icon} />
	</span>
)

type LinkModType = {
	mod?: Mod<'bold'>
}
type LinkIconType = 'chevronRight'

const Link: React.FC<
	LinkProps &
		LinkModType & {
			iconbefore?: LinkIconType
			iconafter?: LinkIconType
		}
> = ({mod, iconbefore, iconafter, children, ...props}) => {
	return (
		<UtilLink {...props} className={styles.link().mod(mod).mergeProps(props)}>
			{iconbefore && <LinkIcon icon={iconbefore} />}
			<LinkText>{children}</LinkText>
			{iconafter && <LinkIcon icon={iconafter} />}
		</UtilLink>
	)
}

const LinkText: React.FC<Span> = (props) => (
	<span {...props} className={styles.linktext.mergeProps(props)} />
)

const LinkIcon: React.FC<{icon: LinkIconType} & Span> = ({icon, ...props}) => (
	<span {...props} className={styles.linkicon.mergeProps(props)}>
		<Icon icon={icon} />
	</span>
)

export const defaultTheme = {
	Container,
	H1,
	H2,
	H3,
	H4,
	H5,
	P,
	Wysiwyg,
	Button,
	ButtonText,
	ButtonIcon,
	Link,
	LinkText,
	LinkIcon
}

export const Theme = createTheme(defaultTheme)
